import { Swords } from 'lucide-react';

import { PanelCard } from './primitives';
import { Skeleton } from '@/components/ui/skeleton';
import { CHAINS, formatInt, type BlockInfo } from '@/lib/mempool';

/** Blocks of deficit shown from the center to either end of the rope. */
const DISPLAY_MAX = 100;
/**
 * A chain is written off once its probability of ever catching up drops
 * below this threshold (gambler's ruin at the current hashrate split).
 */
const FATAL_PROBABILITY = 0.001;

/**
 * Probability that a chain holding `behindShare` of the combined hashrate
 * ever closes a deficit of `deficit` blocks (biased random walk).
 */
function catchUpProbability(behindShare: number, deficit: number): number {
  if (deficit <= 0) return 1;
  if (behindShare >= 0.5) return 1;
  if (behindShare <= 0) return 0;
  return Math.pow(behindShare / (1 - behindShare), deficit);
}

/** Deficit in blocks at which a chain with `share` of the hashrate is doomed. */
function fatalDeficit(share: number): number {
  if (share >= 0.5) return Infinity;
  if (share <= 0) return 0;
  return Math.ceil(Math.log(FATAL_PROBABILITY) / Math.log(share / (1 - share)));
}

function formatShare(share: number): string {
  const pct = share * 100;
  // Show extra precision when the split is nearly tied.
  return `${Math.abs(pct - 50) < 0.5 ? pct.toFixed(2) : pct.toFixed(1)}%`;
}

function KillZone({ side, widthPct }: { side: 'left' | 'right'; widthPct: number }) {
  if (widthPct <= 0) return null;
  return (
    <div
      aria-hidden
      className={`absolute top-0 bottom-0 ${side === 'left' ? 'left-0 rounded-l-full' : 'right-0 rounded-r-full'}`}
      style={{
        width: `${widthPct}%`,
        background:
          'repeating-linear-gradient(45deg, rgba(239,68,68,0.35) 0 6px, rgba(239,68,68,0.08) 6px 12px)',
      }}
    />
  );
}

export function TugOfWar({
  standardTip,
  bip110Tip,
  standardHashrate,
  bip110Hashrate,
}: {
  standardTip?: BlockInfo;
  bip110Tip?: BlockInfo;
  standardHashrate?: number; // H/s
  bip110Hashrate?: number; // H/s
}) {
  if (!standardTip || !bip110Tip) {
    return (
      <PanelCard icon={<Swords className="size-4 text-muted-foreground" />} title="Tug of War">
        <div className="space-y-4">
          <Skeleton className="h-3 w-full rounded-full" />
          <Skeleton className="mx-auto h-4 w-2/3" />
        </div>
      </PanelCard>
    );
  }

  const delta = standardTip.height - bip110Tip.height; // > 0 → Standard ahead
  const deficit = Math.abs(delta);

  let standardShare: number | undefined;
  let bip110Share: number | undefined;
  if (
    standardHashrate !== undefined &&
    bip110Hashrate !== undefined &&
    standardHashrate > 0 &&
    bip110Hashrate > 0
  ) {
    standardShare = standardHashrate / (standardHashrate + bip110Hashrate);
    bip110Share = 1 - standardShare;
  }

  // Per-side kill thresholds: the deficit at which each chain would be doomed.
  const standardFatal = standardShare !== undefined ? fatalDeficit(standardShare) : Infinity;
  const bip110Fatal = bip110Share !== undefined ? fatalDeficit(bip110Share) : Infinity;

  // The behind chain's catch-up chance for the current deficit.
  const behindShare = delta > 0 ? bip110Share : delta < 0 ? standardShare : undefined;
  const catchUp =
    deficit === 0
      ? 1
      : behindShare === undefined
        ? undefined
        : catchUpProbability(behindShare, deficit);

  const fatalForLagger = delta > 0 ? bip110Fatal : standardFatal;
  const isFatal = deficit > 0 && deficit >= fatalForLagger;

  const leader = delta > 0 ? CHAINS.standard : delta < 0 ? CHAINS.bip110 : null;
  const lagger = delta > 0 ? CHAINS.bip110 : delta < 0 ? CHAINS.standard : null;

  // Marker position: 0–100 % along the rope.
  const clamped = Math.max(-DISPLAY_MAX, Math.min(DISPLAY_MAX, delta));
  const positionPct = 50 + (clamped / DISPLAY_MAX) * 50;

  // Kill-zone widths (each zone covers the region beyond that side's fatal line).
  const leftZonePct =
    standardFatal < DISPLAY_MAX ? (1 - standardFatal / DISPLAY_MAX) * 50 : 0;
  const rightZonePct = bip110Fatal < DISPLAY_MAX ? (1 - bip110Fatal / DISPLAY_MAX) * 50 : 0;

  return (
    <PanelCard
      icon={<Swords className="size-4 text-muted-foreground" />}
      title="Tug of War"
      action={
        <span className="font-mono text-xs text-muted-foreground">
          fatal: catch-up odds &lt; {(FATAL_PROBABILITY * 100).toFixed(1)}%
        </span>
      }
    >
      <div
        role="img"
        aria-label={
          delta === 0 || !leader
            ? `Both chains are tied at height ${standardTip.height}.`
            : `${leader.name} (${leader.shortName}) leads by ${deficit} block${deficit === 1 ? '' : 's'}.`
        }
        className="space-y-5"
      >
        {/* Rope */}
        <div className="px-1 pt-7">
          <div className="relative h-3 rounded-full bg-gradient-to-r from-amber-500/40 via-muted to-violet-500/40">
            <KillZone side="left" widthPct={leftZonePct} />
            <KillZone side="right" widthPct={rightZonePct} />

            {/* Center mark */}
            <div
              aria-hidden
              className="absolute top-1/2 left-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-foreground/40"
            />

            {/* Knot / marker */}
            <div
              aria-hidden
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${positionPct}%` }}
            >
              <div
                className={`size-4 rounded-full border-2 bg-background shadow-md transition-all duration-700 ${
                  delta === 0
                    ? 'border-foreground/60'
                    : delta > 0
                      ? 'border-amber-500'
                      : 'border-violet-500'
                } ${isFatal ? 'outline-2 outline-red-500' : ''}`}
              />
              {/* Delta flag */}
              <div
                className={`absolute -top-7 left-1/2 -translate-x-1/2 rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold whitespace-nowrap ${
                  delta === 0
                    ? 'text-muted-foreground'
                    : delta > 0
                      ? CHAINS.standard.classes.softBadge
                      : CHAINS.bip110.classes.softBadge
                }`}
              >
                {delta === 0 ? 'tied' : `+${formatInt(deficit)}`}
              </div>
            </div>
          </div>

          {/* End labels */}
          <div className="mt-2 flex items-start justify-between gap-4 text-xs">
            <div className={CHAINS.standard.classes.text}>
              <span className="font-medium">{CHAINS.standard.shortName}</span>
              {standardShare !== undefined && (
                <span className="ml-1.5 font-mono text-muted-foreground">
                  {formatShare(standardShare)} hashrate
                </span>
              )}
            </div>
            <div className={`text-right ${CHAINS.bip110.classes.text}`}>
              {bip110Share !== undefined && (
                <span className="mr-1.5 font-mono text-muted-foreground">
                  {formatShare(bip110Share)} hashrate
                </span>
              )}
              <span className="font-medium">{CHAINS.bip110.shortName}</span>
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className="text-center text-sm">
          {leader && lagger ? (
            <p className={isFatal ? 'font-medium text-red-500 dark:text-red-400' : ''}>
              <span className={`font-medium ${leader.classes.text}`}>
                {leader.name} ({leader.shortName})
              </span>{' '}
              leads by{' '}
              <span className="font-mono font-medium">
                {formatInt(deficit)} block{deficit === 1 ? '' : 's'}
              </span>
              {catchUp !== undefined && !isFatal && (
                <span className="text-muted-foreground">
                  {' '}
                  — {lagger.shortName} catch-up odds ≈{' '}
                  <span className="font-mono">
                    {catchUp >= 0.995
                      ? '>99'
                      : catchUp < 0.005
                        ? '<1'
                        : Math.round(catchUp * 100)}
                    %
                  </span>
                </span>
              )}
              {isFatal && (
                <span>
                  {' '}
                  — past the fatal deficit of {formatInt(fatalForLagger)} blocks.{' '}
                  {lagger.shortName} is effectively dead at the current hashrate split.
                </span>
              )}
            </p>
          ) : (
            <p className="text-muted-foreground">
              Dead even — both chains at height{' '}
              <span className="font-mono font-medium text-foreground">
                {formatInt(standardTip.height)}
              </span>
              .
            </p>
          )}
        </div>

        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          Rope scale: ±{DISPLAY_MAX} blocks. Odds use a gambler's ruin model — catch-up
          probability assumes each chain keeps its current share of the combined hashrate.
          {standardFatal === Infinity && bip110Fatal === Infinity && hasShares
            ? ' Neither side currently has a fatal deficit: the split is too balanced.'
            : ''}
        </p>
      </div>
    </PanelCard>
  );
}
